import { f7 } from 'framework7-react';
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import { useEffect, useRef, useState, type CSSProperties } from 'react';

/**
 * Phone field with a **drum/wheel** country selector - the dashboard sibling of
 * the storefront's `intl-tel-input`. Both capture a phone the same way (country
 * dial-code + national number → E.164), but the operator UI here is the same
 * Framework7 wheel used for the time picker (see `ChipInput`), not a searchable
 * dropdown: tapping the country pill opens a rolling wheel of every country.
 *
 * Validation mirrors the storefront guest phone: the composed E.164 must be
 * 5–20 chars AND a real, assignable number for the chosen country
 * (libphonenumber `isValid()`, the equivalent of intl-tel-input's precise
 * check). `onChange` reports the E.164 string and that validity so the parent
 * can gate submit.
 *
 * Usage:
 *   <PhonePicker
 *     label="Phone"
 *     value={phone}
 *     defaultCountry={restaurantCountry}   // ISO2, e.g. "DE"
 *     onChange={(e164, valid) => { setPhone(e164); setPhoneValid(valid); }}
 *   />
 */

/** ISO2 → 🇩🇪 flag emoji (regional-indicator pair). */
function flagEmoji(iso: string): string {
  return iso.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

let regionNames: Intl.DisplayNames | null = null;
try {
  regionNames = new Intl.DisplayNames(undefined, { type: 'region' });
} catch {
  regionNames = null;
}

interface Country {
  iso: string;
  dial: string;
  /** Wheel row label, e.g. "🇩🇪 Germany +49". */
  label: string;
}

/** Built once: every country with a calling code, sorted by localized name. */
const COUNTRIES: Country[] = getCountries()
  .map((iso) => {
    const dial = getCountryCallingCode(iso);
    const name = regionNames?.of(iso) ?? iso;
    return { iso, dial, name, label: `${flagEmoji(iso)} ${name} +${dial}` };
  })
  .sort((a, b) => a.name.localeCompare(b.name))
  .map(({ iso, dial, label }) => ({ iso, dial, label }));

const DIAL_BY_ISO = new Map(COUNTRIES.map((c) => [c.iso, c.dial]));
const ISO_VALUES = COUNTRIES.map((c) => c.iso);
const LABEL_VALUES = COUNTRIES.map((c) => c.label);

const DEFAULT_ISO = 'DE';
// National-number digit band (country code excluded). 7 is the floor a real
// number needs; 14 is the E.164 ceiling - anything longer is rejected so a
// mistyped 15+ digit run can't pass.
const MIN_NATIONAL_DIGITS = 7;
const MAX_NATIONAL_DIGITS = 14;
/** Matches --f7-picker-item-height; fallback only - we measure the live row. */
const PICKER_ITEM_HEIGHT = 36;

// Grid, not flex: the number input carries F7's global `width: 100%` (from
// `.list input[type=tel]`), which as a flex-basis fights the row and can
// collapse the field. A grid track (`auto 1fr`) gives the input the remaining
// space no matter its intrinsic width.
const rowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto minmax(0, 1fr)',
  alignItems: 'center',
  gap: 8,
  width: '100%',
};
const pillStyle: CSSProperties = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  flex: '0 0 auto',
  background: 'rgba(120,120,128,0.12)',
  borderRadius: 6,
  padding: '4px 10px',
  border: 'none',
  cursor: 'pointer',
  fontSize: 17,
  lineHeight: '24px',
  color: 'var(--f7-theme-color)',
  whiteSpace: 'nowrap',
};
const numberInputStyle: CSSProperties = {
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  border: '1px solid rgba(120,120,128,0.3)',
  background: 'rgba(120,120,128,0.06)',
  borderRadius: 8,
  fontSize: 17,
  lineHeight: '24px',
  color: 'var(--f7-text-color)',
  padding: '8px 12px',
  outline: 'none',
};

interface PhonePickerProps {
  /** Stored value (E.164, e.g. "+4917012345"). Only read on mount. */
  value?: string;
  /** ISO2 country to preselect when the value can't be parsed (restaurant country). */
  defaultCountry?: string;
  /** Fires with the composed E.164 and whether it's a valid, assignable number. */
  onChange: (e164: string, isValid: boolean) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

function normalizeIso(iso: string | undefined): string {
  const up = iso?.toUpperCase();
  return up && DIAL_BY_ISO.has(up) ? up : DEFAULT_ISO;
}

export default function PhonePicker({
  value = '',
  defaultCountry,
  onChange,
  label,
  placeholder,
  disabled = false,
}: PhonePickerProps) {
  // Seed country + national number from the stored E.164 once. A parseable
  // number wins its own country; otherwise fall back to the restaurant country.
  const [iso, setIso] = useState(() => {
    const parsed = value ? parsePhoneNumberFromString(value) : undefined;
    return normalizeIso(parsed?.country ?? defaultCountry);
  });
  const [national, setNational] = useState(() => {
    const parsed = value ? parsePhoneNumberFromString(value) : undefined;
    return parsed?.nationalNumber ?? value.replace(/[^\d]/g, '');
  });

  const anchorRef = useRef<HTMLInputElement>(null);
  const pickerRef = useRef<ReturnType<typeof f7.picker.create> | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const dial = DIAL_BY_ISO.get(iso) ?? getCountryCallingCode(DEFAULT_ISO);

  // Typing a leading "+<code>" (e.g. "+84") re-detects the country and moves the
  // dial into the pill - same feel as intl-tel-input. AsYouType resolves the
  // country as soon as the code is unambiguous, then exposes the national part;
  // anything without a "+" is treated as a plain national number for the pill's
  // current country.
  const handleNumberInput = (rawInput: string) => {
    // Any typing means the operator owns the field now - stop re-seeding the
    // restaurant-country default over it.
    pristineRef.current = false;
    if (rawInput.trimStart().startsWith('+')) {
      const ayt = new AsYouType();
      ayt.input(rawInput.replace(/[^\d+]/g, ''));
      const detected = ayt.getCountry();
      if (detected) {
        setIso(normalizeIso(detected));
        setNational(ayt.getNationalNumber());
      } else {
        // Code not unambiguous yet (e.g. just "+1"): keep what was typed so the
        // operator can finish it; the country flips once it resolves.
        setNational(rawInput);
      }
      return;
    }
    setNational(rawInput);
  };

  // Report the composed E.164 + validity whenever the country or number
  // changes (and on mount, so a prefilled valid number is known to the parent
  // without the operator touching the field).
  useEffect(() => {
    const digits = national.replace(/[^\d]/g, '');
    const e164 = digits ? `+${dial}${digits}` : '';
    const parsed = e164 ? parsePhoneNumberFromString(e164) : undefined;
    // Validate by POSSIBLE LENGTH (isPossible), NOT the strict per-prefix pattern
    // (isValid) — the strict check rejects real numbers whose exact block
    // libphonenumber can't classify (disproportionately smaller countries, often
    // the 3-digit dial codes), flagging valid numbers as invalid. Mirrors the
    // storefront input's isValidNumber switch. The digit-count bounds stay as a
    // cheap pre-parse sanity net.
    const isValid =
      digits.length >= MIN_NATIONAL_DIGITS &&
      digits.length <= MAX_NATIONAL_DIGITS &&
      e164.length <= 20 &&
      !!parsed?.isPossible();
    onChangeRef.current(e164, isValid);
  }, [iso, national, dial]);

  // Build the country wheel once, anchored to the hidden input. Same drum as the
  // time picker (ChipInput): popover, rotate effect, one column of countries.
  useEffect(() => {
    const inputEl = anchorRef.current;
    if (!inputEl) return;

    // Mouse wheel: the column is a scroll-snap container, so one notch scrolls
    // ~3 rows. Override to move exactly one stop per notch (mirrors ChipInput).
    let wheelTargets: HTMLElement[] = [];
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const el = e.currentTarget as HTMLElement;
      const row = el.querySelector('.picker-item') as HTMLElement | null;
      const h = row?.offsetHeight || PICKER_ITEM_HEIGHT;
      el.scrollBy({ top: (e.deltaY > 0 ? 1 : -1) * h, behavior: 'smooth' });
    };
    const attachWheel = (p: { $el?: { 0?: HTMLElement } }) => {
      const root = p.$el?.[0];
      if (!root) return;
      wheelTargets = Array.from(root.querySelectorAll<HTMLElement>('.picker-items'));
      wheelTargets.forEach((el) => el.addEventListener('wheel', onWheel, { passive: false }));
    };
    const detachWheel = () => {
      wheelTargets.forEach((el) => el.removeEventListener('wheel', onWheel));
      wheelTargets = [];
    };

    const picker = f7.picker.create({
      inputEl,
      openIn: 'popover',
      rotateEffect: true,
      inputReadOnly: true,
      // No toolbar (no Done/close button) — the popover dismisses on an outside
      // tap instead, same as the time wheel in ChipInput.
      toolbar: false,
      closeByOutsideClick: true,
      cols: [{ values: ISO_VALUES, displayValues: LABEL_VALUES, textAlign: 'left' }],
      on: {
        change(_p: unknown, vals: unknown) {
          const next = (vals as string[])[0];
          if (next) setIso(normalizeIso(next));
        },
        opened: attachWheel,
        close: detachWheel,
      },
    });
    pickerRef.current = picker;

    return () => {
      detachWheel();
      picker.destroy();
      pickerRef.current = null;
    };
  }, []);

  // Default the dial code to the restaurant's country. `defaultCountry` can
  // arrive after mount (restaurant config loads async), so re-seed it - but only
  // while the field is still empty, so it never overrides a number the operator
  // has started typing or a country they picked.
  const pristineRef = useRef(!national);
  useEffect(() => {
    if (!pristineRef.current || national) return;
    const target = normalizeIso(defaultCountry);
    setIso((prev) => (prev === target ? prev : target));
  }, [defaultCountry, national]);

  // Keep the wheel's selection in sync if the country is changed elsewhere.
  useEffect(() => {
    const picker = pickerRef.current;
    if (!picker) return;
    const current = picker.value as string[] | undefined;
    if (!current || current[0] !== iso) picker.setValue([iso]);
  }, [iso]);

  // Plain container - NOT F7's `.item-content`/`.item-inner`, whose flex layout
  // collapses the number input to zero width (it expects .item-media/.item-title
  // children, not a custom row).
  return (
    <div
      className="phone-picker"
      style={{ display: 'block', width: '100%', boxSizing: 'border-box', padding: '10px 16px' }}
    >
      <div>
        {label && (
          <div
            style={{ fontSize: 12, color: 'var(--f7-label-text-color, #8e8e93)', marginBottom: 6 }}
          >
            {label}
          </div>
        )}
        <div style={disabled ? { ...rowStyle, opacity: 0.5 } : rowStyle}>
          <button
            type="button"
            className="no-ripple"
            style={pillStyle}
            disabled={disabled}
            aria-label={label}
            onClick={() => pickerRef.current?.open()}
          >
            <span>
              {flagEmoji(iso)} +{dial}
            </span>
            <i className="f7-icons" style={{ fontSize: 12 }}>
              chevron_down
            </i>
            {/* Hidden input F7 anchors the picker popover to (sibling of the
                pill so the popover points at it, never focusable itself). */}
            <input
              ref={anchorRef}
              type="text"
              readOnly
              aria-hidden
              tabIndex={-1}
              style={{
                position: 'absolute',
                left: 0,
                bottom: 0,
                width: 1,
                height: 1,
                opacity: 0,
                pointerEvents: 'none',
              }}
            />
          </button>
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            placeholder={placeholder}
            value={national}
            disabled={disabled}
            style={numberInputStyle}
            onInput={(e) => handleNumberInput((e.target as HTMLInputElement).value)}
          />
        </div>
      </div>
    </div>
  );
}
